import React, { type ChangeEvent, type MouseEvent, type ReactNode, useRef, type JSX } from 'react';
import { Image } from '../../wag-react/core-components/image';
import { Box } from '../Box';
import { Flex } from '../Flex';
import { Typography } from '../Typography';
import mediaPickerIcon from './assets/picker-icon.svg';
type HTMLInputFileElement = HTMLInputElement;
export type Props = Pick<JSX.IntrinsicElements['input'], 'accept' | 'multiple'> & {
  name?: string;
  min?: number;
  max?: number;
  helperTextBlock?: ReactNode;
  onChange?: (event: ChangeEvent<HTMLInputFileElement>) => void;
};
export const MediaPicker = ({
  name,
  min = 0,
  max,
  accept = 'image/*',
  multiple = true,
  helperTextBlock,
  onChange
}: Props) => {
  const inputFileRef = useRef<HTMLInputFileElement>(null);
  const handleOnClick = () => {
    if (inputFileRef && inputFileRef.current) {
      inputFileRef.current.click();
    }
  };
  const handleOnFileChanged = (event: ChangeEvent<HTMLInputFileElement>) => {
    event.persist();
    const {
      files
    } = event.target;
    const numberOfFiles = files.length;
    const isValid = typeof min === 'number' && typeof max === 'number' ? numberOfFiles >= min && numberOfFiles <= max : true;
    if (!isValid) {
      event.preventDefault();
      // Clear the files on the <input type="file" /> element
      /* eslint no-param-reassign: "error" */
      event.target.value = '';
    }
    if (onChange) {
      onChange(event);
    }
  };
  const handleOnFileClick = (event: MouseEvent<HTMLInputFileElement>) => {
    /**
     * Be able to select the same file over and over again...
     * This is a UX improvement so users won't be confused
     * when they clear and upload the same file again and don't see the file
     * being displayed
     */
    (event.target as HTMLInputFileElement).value = '';
  };
  return <Flex onClick={handleOnClick} css={{
    ':hover': {
      cursor: 'pointer'
    }
  }} data-sentry-element="Flex" data-sentry-component="MediaPicker" data-sentry-source-file="index.tsx">
      <Box data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Image hover height="40px" width="40px" src={mediaPickerIcon} alt="media-picker-icon" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
        <input ref={inputFileRef} onChange={handleOnFileChanged} onClick={handleOnFileClick} multiple={multiple} accept={accept} name={name} type="file" hidden />
      </Box>
      <Flex flexDirection="column" pl="8px" css={{
      ':hover': {
        cursor: 'pointer'
      }
    }} data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        {helperTextBlock || <>
            <Typography variant="body2" color="wagGreen" fontWeight="bold" hover>
              Upload photo(s)
            </Typography>
            <Typography variant="body2" hover>
              optional
            </Typography>
          </>}
      </Flex>
    </Flex>;
};